import { Button, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBulb, IconBookmark, IconCheck } from '@tabler/icons-react';
import { useState } from 'react';
import clsx from 'clsx';
import type { AIMessage } from '~/common/vm/protocol';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import { CollapsibleCard } from '../CollapsibleCard';
import { match } from 'ts-pattern';

export function AIKnowledgeSuggestionCard({
  message,
}: {
  message: Extract<AIMessage, { type: 'knowledge-suggestion' }>;
}) {
  const projectId = useProjectId();
  const [saved, setSaved] = useState(false);

  const { mutate: addMultipleManualKnowledge, isPending: isSaving } =
    api.knowledge.addMultipleManualKnowledge.useMutation({
      onSuccess: () => {
        notifications.show({
          title: 'Knowledge saved',
          message: 'Knowledge has been saved successfully',
          color: 'green',
        });
        setSaved(true);
      },
      onError: () => {
        notifications.show({
          title: 'Error',
          message: 'Failed to save knowledge',
          color: 'red',
        });
        setSaved(false);
      },
      onMutate: () => {
        setSaved(false);
      },
    });

  // Create headerButtons array
  const headerButtons =
    projectId && !saved ?
      [
        {
          name: 'Save as Knowledge',
          icon: IconCheck,
          callback: () => addMultipleManualKnowledge({ projectId, items: message.payload.knowledgeItems }),
        },
      ]
    : [];

  return (
    <CollapsibleCard
      icon={IconBulb}
      title="Suggestion"
      headerButtons={headerButtons}
      defaultExpanded={true}
      classNames={{
        icon: 'text-gray-500 dark:text-gray-400',
      }}
    >
      <div className="space-y-1">
        <div className="mx-1 rounded px-2 py-1.5">
          <Text
            size="sm"
            className="leading-relaxed"
          >
            {message.content}
          </Text>
        </div>

        {match(message.actionId)
          .with('save-knowledge', () => {
            return (
              <div className="mx-1 rounded px-2 py-1.5">
                <div className="space-y-2">
                  <div className="flex items-center space-x-1.5">
                    <IconBookmark
                      size={12}
                      className="text-amber-600"
                    />
                    <Text
                      size="sm"
                      className="font-medium"
                    >
                      Knowledge Items to Save
                    </Text>
                  </div>
                  <ul className="space-y-1.5">
                    {message.payload?.knowledgeItems?.map((knowledge: string, index: number) => (
                      <li
                        key={index}
                        className="flex items-start space-x-1.5"
                      >
                        <div className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-gray-400 dark:bg-gray-600" />
                        <Text
                          size="sm"
                          className="leading-relaxed"
                        >
                          {knowledge}
                        </Text>
                      </li>
                    ))}
                  </ul>
                  {!!projectId && (
                    <Button
                      variant="light"
                      color="gray"
                      size="xs"
                      loading={isSaving}
                      disabled={saved}
                      onClick={() => addMultipleManualKnowledge({ projectId, items: message.payload.knowledgeItems })}
                      leftSection={saved ? <IconCheck size={12} /> : <IconBookmark size={12} />}
                      className={clsx(
                        'transition-all duration-200',
                        saved ?
                          'border-gray-300 bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        : 'border-gray-300 text-gray-700 dark:text-gray-300',
                      )}
                    >
                      {saved ? 'Knowledge Saved' : 'Save to Knowledge Base'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })
          .exhaustive()}
      </div>
    </CollapsibleCard>
  );
}
